import { BASE_URL, withBase } from './base';
import {
  DEFAULT_OG_IMAGE,
  PAGE_SEO,
  SITE_ADDRESS,
  SITE_EMAIL,
  SITE_NAME,
  SITE_NAME_CORP,
  SITE_NAME_LEGAL,
  SITE_PHONE,
  type PageSeo,
  type SeoPageId,
} from './seo-data';

export type { SeoPageId } from './seo-data';
export type SeoOverride = {
  title: string;
  description: string;
  path?: string;
  image?: string;
  type?: 'website' | 'article';
  robots?: string;
  keywords?: string;
};

function siteOrigin(): string {
  return window.location.origin;
}

function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const origin = siteOrigin();
  if (pathOrUrl.startsWith(BASE_URL) || pathOrUrl.startsWith('/')) {
    return `${origin}${pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`}`;
  }
  return `${origin}${withBase(pathOrUrl)}`;
}

function currentUrl(path?: string): string {
  if (path) return absoluteUrl(path.startsWith('http') ? path : withBase(path));
  return `${window.location.origin}${window.location.pathname}${window.location.search}`;
}

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function setJsonLd(data: Record<string, unknown>) {
  let el = document.getElementById('seo-jsonld') as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = 'seo-jsonld';
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function organizationNode(homeUrl: string, logoUrl: string) {
  return {
    '@type': 'Organization',
    '@id': `${homeUrl}#organization`,
    name: SITE_NAME_CORP,
    alternateName: [SITE_NAME, SITE_NAME_LEGAL],
    url: homeUrl,
    logo: logoUrl,
    telephone: SITE_PHONE,
    email: SITE_EMAIL,
    address: {
      '@type': 'PostalAddress',
      streetAddress: SITE_ADDRESS,
      addressLocality: '광명시',
      addressRegion: '경기도',
      addressCountry: 'KR',
    },
  };
}

export function applySeo(input: SeoOverride): void {
  const url = currentUrl(input.path);
  const image = absoluteUrl(input.image ? withBase(input.image) : withBase(DEFAULT_OG_IMAGE));
  const robots = input.robots ?? 'index, follow';
  const ogType = input.type ?? 'website';
  const homeUrl = absoluteUrl(withBase('/'));

  document.title = input.title;
  setMeta('name', 'description', input.description);
  if (input.keywords) setMeta('name', 'keywords', input.keywords);
  setMeta('name', 'robots', robots);
  setMeta('property', 'og:title', input.title);
  setMeta('property', 'og:description', input.description);
  setMeta('property', 'og:url', url);
  setMeta('property', 'og:image', image);
  setMeta('property', 'og:type', ogType);
  setMeta('name', 'twitter:title', input.title);
  setMeta('name', 'twitter:description', input.description);
  setMeta('name', 'twitter:image', image);
  setLink('canonical', url.replace(/#.*$/, ''));

  const logoUrl = absoluteUrl(withBase('/assets/icons/logo-mark.svg'));
  if (input.type === 'article') {
    setJsonLd({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: input.title,
      description: input.description,
      url,
      inLanguage: 'ko-KR',
      publisher: organizationNode(homeUrl, logoUrl),
    });
    return;
  }

  setJsonLd({
    '@context': 'https://schema.org',
    '@graph': [
      organizationNode(homeUrl, logoUrl),
      {
        '@type': 'WebPage',
        name: input.title,
        description: input.description,
        url,
        inLanguage: 'ko-KR',
        isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: homeUrl },
      },
    ],
  });
}

export function applyPageSeo(page: SeoPageId, extra?: Partial<SeoOverride>): void {
  const seo: PageSeo = PAGE_SEO[page];
  applySeo({
    title: extra?.title ?? seo.title,
    description: extra?.description ?? seo.description,
    path: extra?.path,
    image: extra?.image,
    type: extra?.type,
    robots: extra?.robots ?? seo.robots,
    keywords: extra?.keywords ?? seo.keywords,
  });
}
